import { useEffect, useRef, useState } from "react"

import { ApiError } from "@/api/client"
import {
  useAdminAccountRequests,
  useUpdateAccountRequest,
  type AccountRequest,
} from "@/api/hooks/requests"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

const FULFILLMENT_INPUT_LABELS = new Set([
  "Ad account details",
  "Edit ad account details",
])

function formatDate(value: string | null): string {
  if (!value) return "—"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

function accountRequestStatusLabel(status: string): string {
  if (status === "pending") return "New"
  if (status === "in_progress") return "In Progress"
  if (status === "completed") return "Complete"
  if (status === "cancelled") return "Cancelled"
  return status.replace(/_/g, " ")
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "completed"
      ? "bg-[#1e4d3e] text-[#4ade80]"
      : status === "cancelled"
        ? "bg-[#4a2a2a] text-[#f87171]"
        : status === "in_progress"
          ? "bg-[#3a3a1e] text-[#fbbf24]"
          : "bg-[#2d3a4a] text-[#93c5fd]"
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone}`}
    >
      {accountRequestStatusLabel(status)}
    </span>
  )
}

export default function AccountRequestsTab() {
  const query = useAdminAccountRequests()
  const updateMutation = useUpdateAccountRequest()
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [notes, setNotes] = useState<Record<number, string>>({})
  const [editingId, setEditingId] = useState<number | null>(null)
  const clearedAutofocusRef = useRef(false)

  // Browsers often park focus in the first text input on load (and trigger
  // password managers). Blur once after the list appears — not on later polls.
  useEffect(() => {
    if (clearedAutofocusRef.current || query.isLoading) return
    clearedAutofocusRef.current = true
    const timer = window.setTimeout(() => {
      const active = document.activeElement
      if (
        active instanceof HTMLInputElement &&
        FULFILLMENT_INPUT_LABELS.has(active.getAttribute("aria-label") ?? "")
      ) {
        active.blur()
      }
    }, 0)
    return () => window.clearTimeout(timer)
  }, [query.isLoading])

  const act = async (
    id: number,
    status: "in_progress" | "cancelled" | "completed",
    fulfillment_note?: string,
  ) => {
    setError(null)
    setBusyId(id)
    try {
      await updateMutation.mutateAsync({ id, status, fulfillment_note })
      if (status === "completed") {
        setNotes((prev) => {
          const next = { ...prev }
          delete next[id]
          return next
        })
        setEditingId(null)
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Action failed",
      )
    } finally {
      setBusyId(null)
    }
  }

  const startEdit = (row: AccountRequest) => {
    setError(null)
    setEditingId(row.id)
    setNotes((prev) => ({
      ...prev,
      [row.id]: row.fulfillment_note ?? "",
    }))
  }

  const cancelEdit = (id: number) => {
    setEditingId(null)
    setNotes((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const rows = query.data?.data ?? []

  return (
    <div className="space-y-4">
      {error ? (
        <Alert
          variant="destructive"
          className="border-[#663333] bg-[#4a2a2a] text-[#f87171]"
        >
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <p className="py-8 text-center text-[#b0b0b0]">Loading...</p>
      ) : query.isError ? (
        <p className="py-8 text-center text-[#f87171]">Failed to load requests</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-[#888]">No account requests yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row: AccountRequest) => {
            const busy = busyId === row.id
            const note = notes[row.id] ?? ""
            const editing = editingId === row.id
            return (
              <div
                key={row.id}
                className="rounded-lg border border-[#555] bg-[#3a3a3a] p-4"
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-[#e0e0e0]">
                        {row.requester?.username ?? `User #${row.requester_id}`}
                      </span>
                      <StatusPill status={row.status} />
                      <span className="text-xs text-[#888]">
                        Submitted {formatDate(row.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-[#b0b0b0]">
                      Network:{" "}
                      <span className="text-[#e0e0e0]">{row.network_name}</span>
                      {" · "}
                      Agency:{" "}
                      <span className="text-[#e0e0e0]">{row.agency}</span>
                      {" · "}
                      TopUp:{" "}
                      <span className="text-[#e0e0e0]">
                        USD{" "}
                        {Number.isFinite(Number(row.top_up_amount))
                          ? String(Math.trunc(Number(row.top_up_amount)))
                          : String(row.top_up_amount)}
                      </span>
                    </p>
                    {row.status === "completed" &&
                    row.fulfillment_note &&
                    !editing ? (
                      <p className="mt-2 whitespace-pre-wrap rounded border border-[#555] bg-[#2d2d2d] p-3 text-sm text-[#e0e0e0]">
                        {row.fulfillment_note}
                      </p>
                    ) : null}
                  </div>

                  {row.status === "pending" ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={() => void act(row.id, "in_progress")}
                        className="bg-[#667eea] text-white hover:bg-[#5568d3]"
                      >
                        In Progress
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={() => void act(row.id, "cancelled")}
                        className="bg-[#7f1d1d] text-white hover:bg-[#991b1b]"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : null}

                  {row.status === "in_progress" ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={() => void act(row.id, "cancelled")}
                        className="bg-[#7f1d1d] text-white hover:bg-[#991b1b]"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : null}

                  {row.status === "completed" && !editing ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={() => startEdit(row)}
                        className="bg-[#4a5568] text-white hover:bg-[#3d4654]"
                      >
                        Edit
                      </Button>
                    </div>
                  ) : null}
                </div>

                {row.status === "in_progress" ? (
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      value={note}
                      onChange={(e) =>
                        setNotes((prev) => ({
                          ...prev,
                          [row.id]: e.target.value,
                        }))
                      }
                      placeholder="Account name · ID"
                      aria-label="Ad account details"
                      name={`fulfillment_note_${row.id}`}
                      autoComplete="off"
                      autoFocus={false}
                      data-1p-ignore
                      data-lpignore="true"
                      className="min-w-0 flex-1 border-[#555] bg-[#2d2d2d] text-white"
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={busy || !note.trim()}
                      onClick={() => void act(row.id, "completed", note.trim())}
                      className="shrink-0 bg-[#3d8b5c] text-white hover:bg-[#337350]"
                    >
                      Complete
                    </Button>
                  </div>
                ) : null}

                {row.status === "completed" && editing ? (
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      value={note}
                      onChange={(e) =>
                        setNotes((prev) => ({
                          ...prev,
                          [row.id]: e.target.value,
                        }))
                      }
                      placeholder="Account name · ID"
                      aria-label="Edit ad account details"
                      name={`fulfillment_note_edit_${row.id}`}
                      autoComplete="off"
                      autoFocus={false}
                      data-1p-ignore
                      data-lpignore="true"
                      className="min-w-0 flex-1 border-[#555] bg-[#2d2d2d] text-white"
                    />
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy || !note.trim()}
                        onClick={() =>
                          void act(row.id, "completed", note.trim())
                        }
                        className="bg-[#3d8b5c] text-white hover:bg-[#337350]"
                      >
                        {busy ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={() => cancelEdit(row.id)}
                        className="bg-[#4a5568] text-white hover:bg-[#3d4654]"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
